import { Backdrop, CircularProgress, Box, Grid, Typography, TextField, Button } from '@mui/material';
import { useRef, useState } from 'react';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { getPatient } from '@/graphql/queries';
import { updatePatient } from '@/graphql/mutations';
import { updateUserAttributes } from 'aws-amplify/auth';
import { getWalletState } from '../../mynumber/constants/Api';
import { web3APIClient } from '@/client/axios';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { GetPatientQuery, UpdatePatientMutation } from '@/API';
import { executeQuery } from '@/client/graphql';

export function InputTokenForm(props: any) {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  let tokenFormRef = useRef<HTMLInputElement>(null);
  const { handleConfirmChangeEmail } = useCognitoAuth();

  const handleTokenSubmit = async () => {
    const token = tokenFormRef.current!.value.trim();
    if (token !== '') {
      setOpen(true);
      try {
        await handleConfirmChangeEmail(token);
        // web3:患者情報更新API呼出(メールアドレスの変更)
        if (await getWalletState()) {
          const session = await fetchAuthSession({ forceRefresh: true });
          const idToken = session.tokens?.idToken ?? '';
          const config = {
            headers: { Authorization: idToken.toString() }
          };
          await web3APIClient.put('patients/info', null, config);
        }
        // メールアドレス検証後にDynamoDB保存処理を実行
        await saveToDynamoDB();
        props.linkToMypage();
      } catch (e) {
        if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
          Sentry.captureException(e);
        }
        setErrorMessage('ご入力いただいた認証コードが異なります。再度ご確認ください。');
      } finally {
        setOpen(false);
      }
    }
  };

  const ErrorMessage = (props: { message: string | undefined }) => {
    if (!props.message) return null;
    return (
      <Typography color="error" style={{ fontSize: '12px', marginLeft: '4px' }}>
        {props.message}
      </Typography>
    );
  };

  // DynamoDB保存処理
  async function saveToDynamoDB() {
    try {
      const cognitoId = (await fetchUserAttributes()).sub!;
      const patientInfo = (await executeQuery<GetPatientQuery>(getPatient, { id: cognitoId }, 'userPool')).getPatient!;
      await executeQuery<UpdatePatientMutation>(
        updatePatient,
        { input: { id: patientInfo.id, phoneNumber: props.phoneNumber, email: props.email } },
        'userPool'
      );
    } catch (e: any) {
      if (e instanceof Error && notMatchCustomErrorMessage(e.name)) {
        Sentry.captureException(e);
      }

      setErrorMessage('ご入力いただいた認証コードが異なります。再度ご確認ください。');
    }
  }

  const SubmitButton = (props: { label: string; disabled?: boolean }) => {
    return (
      <Button
        disabled={props.disabled}
        type="submit"
        fullWidth
        sx={{ height: '48px', color: '#FFFFFF', textAlign: 'center', margin: '32px auto' }}
        className="bg-primary flex relative rounded"
        onClick={() => handleTokenSubmit()}
      >
        <div className="text-base m-auto font-bold">{props.label}</div>
      </Button>
    );
  };

  const handleResendSignUpCode = async () => {
    setOpen(true);
    try {
      await updateUserAttributes({ userAttributes: { email: props.email } });
    } catch (error) {
      console.log(error);
      if (typeof error === 'object' && error != null && 'name' in error) {
        switch (error.name) {
          case 'LimitExceededException':
            setErrorMessage('問題が発生しました。少し時間をおいて再度お試しください。');
            break;
          default:
            setErrorMessage('問題が発生しました。再度確認して入力してください。');
        }
      } else {
        setErrorMessage('問題が発生しました。再度確認して入力してください。');
      }
    } finally {
      setOpen(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} spacing={2}>
          <Grid item xs={0} md={3} sm={2} />
          <Grid item xs={12} md={6} sm={8}>
            <Grid container direction={'column'} spacing={2}>
              <Grid item xs={12} mt={2} sx={{ textAlign: 'center', maxWidth: '100% !important' }}>
                <Typography mb={2} pt={2} fontSize={'16px'} className="font-bold">
                  2段階認証
                </Typography>
                <Typography mb={2} pt={2} fontSize={'14px'}>
                  認証コードをメールでお送りしました
                  <br />
                  メールに記載されている認証コードを
                  <br />
                  入力してください
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'left', maxWidth: '100% !important' }}>
                <Typography m={0.5} fontSize={'14px'} className="font-bold">
                  認証コード
                </Typography>
                <TextField
                  type="password"
                  inputRef={tokenFormRef}
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: '#fff' }}
                  onChange={(e) => {
                    if (e.currentTarget.value.trim() === '') {
                      setIsSubmitDisabled(true);
                    } else if (isSubmitDisabled) {
                      setIsSubmitDisabled(false);
                    }
                  }}
                />
                <SubmitButton disabled={isSubmitDisabled} label={'送信する'} />
                {errorMessage && <ErrorMessage message={errorMessage} />}
                <Grid item xs={12} mt={2} sx={{ textAlign: 'center', maxWidth: '100% !important' }}>
                  <button
                    onClick={() => handleResendSignUpCode()}
                    style={{ color: '#1976d2', borderBottom: '1px solid', fontSize: '12px' }}
                  >
                    認証コードの再送はこちら
                  </button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
